import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  FiMessageCircle,
  FiRefreshCw,
  FiPaperclip,
} from "react-icons/fi";
import { GoGlobe, GoPlus } from "react-icons/go";
import {
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaSpinner,
  FaArrowLeft,
  FaTimes,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import {
  chatService,
  authService,
  documentService,
} from "../../shared/services/apis";
import type { ChatSession, Message } from "../../shared/types";
import type { StreamUploadResult } from "../../shared/types/document";
import { DocumentStatus } from "../../constants/enum";
import { FaAngleRight } from "react-icons/fa6";
import IframeViewer from "../../shared/ui/urlOpener/IFrameViewer";
import ChatMarkdown from "../../shared/ui/chatmarkdown/ChatMarkdown";
import { PiDotsSixBold } from "react-icons/pi";
import { AiOutlineFileSync } from "react-icons/ai";
import toast from "react-hot-toast";

// Enhanced Chat type that aligns with the API
export type Chat = {
  id: string;
  title: string;
  tag?: string;
  date: "today" | "yesterday" | "older";
  tenant: string;
  tenantId: string;
  lastMessage?: string;
  messageCount?: number;
  updatedAt: string;
  createdAt: string;
  status?: "active" | "processing" | "completed" | "error";
};

 

// Props interface for the component
interface AllChatsDropdownProps {
  onChatSelect?: (chat: Chat) => void;
  onNewChat?: (chat: Chat) => void;
  className?: string;
}

// Utility function to categorize chats by date
const categorizeByDate = (
  dateString: string
): "today" | "yesterday" | "older" => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const isToday = date.toDateString() === today.toDateString();
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) return "today";
  if (isYesterday) return "yesterday";
  return "older";
};

// Function to determine chat status based on messages
const determineChatStatus = (
  messages: Message[]
): "active" | "processing" | "completed" | "error" => {
  if (messages.length === 0) return "active";

  const lastMessage = messages[messages.length - 1];
  const hasPendingMessages = messages.some(
    (msg) => msg.responseStatus === "PENDING"
  );
  const hasFailedMessages = messages.some(
    (msg) => msg.responseStatus === "FAILURE"
  );

  if (hasPendingMessages) return "processing";
  if (hasFailedMessages) return "error";
  if (lastMessage?.responseStatus === "SUCCESS") return "completed";

  return "active";
};

// Convert ChatSession to Chat format with status
const convertSessionToChat = (session: ChatSession): Chat => {
  const status = determineChatStatus(session.messages || []);

  return {
    id: session.id,
    title: session.title || "Untitled Chat",
    tenant: session.tenant?.name || "Unknown Tenant",
    tenantId: session.tenantId,
    date: categorizeByDate(session.updatedAt),
    lastMessage:
      session.messages && session.messages.length > 0
        ? session.messages[0]?.query || ""
        : "",
    messageCount: session.messages?.length || 0,
    updatedAt: session.updatedAt,
    createdAt: session.createdAt,
    status,
  };
};

const TenantChat = ({ onChatSelect, onNewChat }: AllChatsDropdownProps) => {
  const { tenantId } = useParams<{ tenantId: string }>();
  // console.log(tenantId, "tenantId");

  // UI State
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"list" | "chat">("list");
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Dragging state
  const [position, setPosition] = useState({
    x: window.innerWidth - 500, // Start from right side
    y: 50,
  });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Chat List State
  const [groupedChats, setGroupedChats] = useState<Record<string, Chat[]>>({
    today: [],
    yesterday: [],
    older: [],
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Loading States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creatingChat, setCreatingChat] = useState(false);

  // Chat Interface State
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageInput, setMessageInput] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [, setMessagesError] = useState<string | null>(null);
  const [waitingForReply, setWaitingForReply] = useState(false);
  const [websearch, setWebSearch] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);

  // New states for document upload flow
  const [fileSelectedButNotUploaded, setFileSelectedButNotUploaded] =
    useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentProcessing, setDocumentProcessing] = useState(false);
  const [uploadedDocumentId, setUploadedDocumentId] = useState<string | null>(
    null
  );
  const [documentPollingInterval, setDocumentPollingInterval] = useState<
    number | null
  >(null);


  // Existing polling state for messages
  const [pollingInterval, setPollingInterval] = useState<ReturnType<typeof setInterval> | number|null>(null);

  const [lastMessageCount, setLastMessageCount] = useState<number>(0);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const documentPollingRef = useRef<number | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const navigate = useNavigate();

  // Get auth data for upload
  const currentUser = authService.getCurrentUser();
  const userId = currentUser?.id || "";
  const brandId = currentUser?.brandId || "";

  const uploadedDocumentIdRef = useRef<string | null>(null);
  useEffect(() => {
    uploadedDocumentIdRef.current = uploadedDocumentId;
  }, [uploadedDocumentId]);

  // Dragging functionality (unchanged)
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    setOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (dragging) {
        const newX = e.clientX - offset.x;
        const newY = e.clientY - offset.y;

        // Get viewport size
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;

        // Get chat panel size (480px width, max 80vh height)
        const panelWidth = 480;
        const panelHeight = Math.min(screenHeight * 0.8, 640); // 80vh or 640px max

        // Clamp values so it stays inside screen
        const clampedX = Math.max(0, Math.min(newX, screenWidth - panelWidth));
        const clampedY = Math.max(
          0,
          Math.min(newY, screenHeight - panelHeight)
        );

        setPosition({ x: clampedX, y: clampedY });
      }
    },
    [dragging, offset]
  );

  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, handleMouseMove, handleMouseUp]);

  // Memoize filtered chats for better performance
  const filteredChatsData = useMemo(() => {
    const result: Record<string, Chat[]> = {
      today: [],
      yesterday: [],
      older: [],
    };

    Object.keys(groupedChats).forEach((key) => {
      result[key] = groupedChats[key].filter(
        (chat) =>
          chat.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          chat.tenant.toLowerCase().includes(searchTerm.toLowerCase()) ||
          chat.lastMessage?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });

    return result;
  }, [groupedChats, searchTerm]);

  // Optimized scroll to bottom with better performance
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, []);

  // Debounced scroll to bottom to prevent excessive calls
  const debouncedScrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      setTimeout(scrollToBottom, 50);
    });
  }, [scrollToBottom]);

  // Load chats from API with optimistic updates
  const loadChats = useCallback(async () => {
    if (!open || loading) return;

    setLoading(true);
    setError(null);

    try {
      const sessions = await chatService.getSessions();
      const chats = sessions
        .filter((session) => session.tenantId === tenantId)
        .map(convertSessionToChat);

      // Group chats by date
      const grouped: Record<string, Chat[]> = {
        today: [],
        yesterday: [],
        older: [],
      };

      chats.forEach((chat) => {
        grouped[chat.date].push(chat);
      });

      // Sort each group by updatedAt (most recent first)
      Object.keys(grouped).forEach((key) => {
        grouped[key].sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      });

      setGroupedChats(grouped);
    } catch (err) {
      console.error("Failed to load chats:", err);
      setError("Failed to load chats. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [open, loading]);

  // Function to update chat status in the list
  const updateChatStatus = useCallback(
    (
      chatId: string,
      newStatus: "active" | "processing" | "completed" | "error"
    ) => {
      setGroupedChats((prev) => {
        const updated = { ...prev };

        Object.keys(updated).forEach((dateKey) => {
          updated[dateKey] = updated[dateKey].map((chat) =>
            chat.id === chatId ? { ...chat, status: newStatus } : chat
          );
        });

        return updated;
      });
    },
    []
  );

  // Existing function to check for new messages
  const checkForNewMessages = useCallback(
    async (chatId: string) => {
      // console.log("check for new message")
      try {
        // console.log("api se phle")
        const chatMessages = await chatService.getMessages(chatId);

        // Check if we got new messages
        // console.log(chatMessages.length,lastMessageCount,"yeh hai dono ka length")
        if (chatMessages.length > lastMessageCount) {
          const newMessages = chatMessages.slice(lastMessageCount);
          const hasAIReply = newMessages.some(
            (msg) => msg.responseStatus === "SUCCESS"
          );
          // console.log(hasAIReply,"hasAIReplyjnvnn")

          if (hasAIReply) {
            // Update messages and stop polling
            setMessages(chatMessages);
            setWaitingForReply(false);
            setLastMessageCount(chatMessages.length);

            // Update chat status to completed
            updateChatStatus(chatId, "completed");

            // Clear the polling interval
            // console.log(pollingInterval,"pollingintervallllll")
            if (pollingInterval) {
              clearInterval(pollingInterval);
              setPollingInterval(null);
            }

            // If this was a document upload dummy message and it's now complete, clear file states
            if (
              documentProcessing &&
              newMessages.some((msg) =>
                msg.query?.includes("Uploaded document:")
              )
            ) {
              setDocumentProcessing(false);
              setUploadedDocumentId(null);
              setSelectedFile(null);
              setFilePreview(null);
              setFileSelectedButNotUploaded(false);
              setUploadingDocument(false);
              stopDocumentPolling();
            }

            debouncedScrollToBottom();
            return true; // Reply received
          } else {
            // Update message count but keep polling
            setMessages(chatMessages);
            setLastMessageCount(chatMessages.length);
          }
        }
        return false; // No reply yet
      } catch (err) {
        console.error("Failed to check for new messages:", err);
        // Update chat status to error on failure
        updateChatStatus(chatId, "error");
        // Clear document states on error
        if (documentProcessing || uploadingDocument) {
          setDocumentProcessing(false);
          setUploadedDocumentId(null);
          setSelectedFile(null);
          setFilePreview(null);
          setFileSelectedButNotUploaded(false);
          setUploadingDocument(false);
          stopDocumentPolling();
          toast.error("Document processing failed. Please try again.");
        }
        return false;
      }
    },
    [
      lastMessageCount,
      pollingInterval,
      debouncedScrollToBottom,
      updateChatStatus,
      documentProcessing,
      uploadingDocument,
    ]
  );

  // Start polling for chat replies
  const startPollingForReply = useCallback(
    (chatId: string) => {
      // Clear any existing interval
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }

      // Update chat status to processing
      updateChatStatus(chatId, "processing");

      // Start new polling interval
      
      const interval = setInterval(async () => {
        const replyReceived = await checkForNewMessages(chatId);
        if (replyReceived) {
          clearInterval(interval);
          setPollingInterval(null);
        }
      }, 2000); // Poll every 2 seconds

      setPollingInterval(interval);

      // Auto-stop polling after 5 minutes to prevent infinite polling
      setTimeout(() => {
        if (interval) {
          clearInterval(interval);
          setPollingInterval(null);
          setWaitingForReply(false);
          updateChatStatus(chatId, "error");
          // console.log("Polling stopped due to timeout");
        }
      }, 300000);
    },
    [pollingInterval, checkForNewMessages, updateChatStatus]
  );
  const stopDocumentPolling = useCallback(() => {
    if (documentPollingRef.current) {
      console.log(
        "Clearing document polling interval",
        documentPollingRef.current
      );
      clearInterval(documentPollingRef.current);
      documentPollingRef.current = null;
    } else {
      console.log("No document polling interval to clear");
    }
    setDocumentPollingInterval(null);
  }, []);

  // New: Document status polling
  const checkDocumentStatus = useCallback(async () => {
    if (!tenantId || !uploadedDocumentIdRef.current) {
      console.log("Polling skipped: missing tenantId or uploadedDocumentId");
      return;
    }

    
    const currentDocId = uploadedDocumentIdRef.current;

    try {
      const updatedDocuments = await documentService.getDocumentsByTenant(
        tenantId
      );

      // console.log("Polling documents:", updatedDocuments.length);

      const matchingDoc = updatedDocuments.find(
        (doc) => doc.id === currentDocId
      );
      if (!matchingDoc) {
        // console.log("No matching document found for id:", currentDocId);
        return;
      }

      // console.log(
      //   "Matching document ingestionStatus:",
      //   matchingDoc.ingestionStatus
      // );

      setMessages((prevMessages) =>
        prevMessages.map((msg) => {
          if (msg.metadata?.documentId === currentDocId) {
            if (matchingDoc.ingestionStatus === DocumentStatus.COMPLETED) {
              return {
                ...msg,
                responseStatus: "SUCCESS",
                result: "Document uploaded and processed successfully.",
                updatedAt: new Date().toISOString(),
              };
            } else if (matchingDoc.ingestionStatus === DocumentStatus.FAILED) {
              return {
                ...msg,
                responseStatus: "FAILURE",
                result: "Document processing failed.",
                updatedAt: new Date().toISOString(),
              };
            } else if (
              matchingDoc.ingestionStatus === DocumentStatus.IN_PROGRESS ||
              matchingDoc.ingestionStatus === DocumentStatus.PENDING
            ) {
              return { ...msg, responseStatus: "PENDING" };
            }
          }
          return msg;
        })
      );

      if (
        matchingDoc.ingestionStatus === DocumentStatus.COMPLETED ||
        matchingDoc.ingestionStatus === DocumentStatus.FAILED
      ) {
        // console.log(
        //   "Document processing finished with status:",
        //   matchingDoc.ingestionStatus
        // );
        stopDocumentPolling();

        setDocumentProcessing(
          matchingDoc.ingestionStatus !== DocumentStatus.COMPLETED
        );

        const chatId = uploadedDocumentIdRef.current || "";

        if (matchingDoc.ingestionStatus === DocumentStatus.COMPLETED) {
          updateChatStatus(chatId, "completed");
          toast.success("Document processed successfully!");
        
        } else {
          updateChatStatus(chatId, "error");
          toast.error("Document processing failed.");
        }
          setTimeout(() => {
            setUploadedDocumentId(null);
            setSelectedFile(null);
            setFilePreview(null);
            setFileSelectedButNotUploaded(false);
            setUploadingDocument(false);
          }, 1000);
      }
    } catch (err) {
      console.error("Document polling failed:", err);
      stopDocumentPolling();
      toast.error("Failed to check document status.");
        setTimeout(() => {
            setUploadedDocumentId(null);
            setSelectedFile(null);
            setFilePreview(null);
            setFileSelectedButNotUploaded(false);
            setUploadingDocument(false);
          }, 1000);
    }
  }, [tenantId, stopDocumentPolling, updateChatStatus]);

  const startDocumentPolling = useCallback(() => {
    if (documentPollingRef.current) {
      console.log(
        "Document polling already started:",
        documentPollingRef.current
      );
      return;
    }
    documentPollingRef.current = window.setInterval(checkDocumentStatus, 3000);
    console.log(
      "Document polling started with interval id:",
      documentPollingRef.current
    );
    setDocumentPollingInterval(documentPollingRef.current);
  }, [checkDocumentStatus]);

  useEffect(() => {
    if (uploadedDocumentId) {
      startDocumentPolling();
    }
  }, [uploadedDocumentId, startDocumentPolling]);

  // Function to check if there's a pending system response
  const checkForPendingResponse = useCallback((messages: Message[]) => {
    if (messages.length === 0) return false;

    // Check if the last message is pending and needs polling for response
    const lastMessage = messages[messages.length - 1];
    const isLastMessagePending =
      lastMessage && lastMessage.responseStatus === "PENDING";

    // If last message is pending, check if it's recent (within last 5 minutes)
    // This indicates a potential response that's still being processed
    if (isLastMessagePending) {
      const lastMessageTime = new Date(lastMessage.createdAt).getTime();
      const currentTime = new Date().getTime();
      const timeDiffMinutes = (currentTime - lastMessageTime) / (1000 * 60);

      // If message was sent within last 5 minutes, consider it as potentially pending
      return timeDiffMinutes <= 5;
    }

    return false;
  }, []);

  // Load messages for a chat with smooth transition
  const loadMessages = useCallback(
    async (chatId: string) => {
      if (loadingMessages) return;

      setLoadingMessages(true);
      setMessagesError(null);
      setMessages([]); // Clear messages immediately for smoother transition

      try {
        const chatMessages = await chatService.getMessages(chatId);
        setMessages(chatMessages);
        setLastMessageCount(chatMessages.length); // Track initial message count

        // Check if there's a pending response after loading messages
        const hasPendingResponse = checkForPendingResponse(chatMessages);

        if (hasPendingResponse) {
          setWaitingForReply(true);
          // Start polling to check for new responses
          startPollingForReply(chatId);
        } else {
          // Update chat status based on current messages
          const status = determineChatStatus(chatMessages);
          updateChatStatus(chatId, status);
        }

        debouncedScrollToBottom();
      } catch (err) {
        console.error("Failed to load messages:", err);
        setMessagesError("Failed to load messages. Please try again.");
        updateChatStatus(chatId, "error");
      } finally {
        setLoadingMessages(false);
      }
    },
    [
      loadingMessages,
      debouncedScrollToBottom,
      checkForPendingResponse,
      startPollingForReply,
      updateChatStatus,
    ]
  );

  // Updated: Check if there are any pending messages or document processing
  const hasPendingMessages = useMemo(() => {
    return (
      messages.some((msg) => msg.responseStatus === "PENDING") ||
      documentProcessing ||
      uploadingDocument
    );
  }, [messages, documentProcessing, uploadingDocument]);

  //  Handle file upload using documentService (replaces old handleFileUpload)
  const handleFileUpload = useCallback(async () => {
    if (
      !selectedFile ||
      !selectedChat ||
      uploadingDocument ||
      hasPendingMessages ||
      !userId ||
      !brandId
    ) {
      toast.error("Cannot upload: Missing user data or pending operations.");
      return;
    }

    // const dummyQuery = ` ${selectedFile.name}`;
    setUploadingDocument(true);
    setError(null);

    // Create dummy user message for document upload
    // const dummyMessage: Message = {
    //   id: `doc-${Date.now()}`,
    //   sessionId: selectedChat.id,
    //   query: dummyQuery,
    //   responseStatus: "PENDING",
    //   createdAt: new Date().toISOString(),
    //   metadata: {
    //     fileName: selectedFile.name,
    //     fileSize: selectedFile.size,
    //     mimeType: selectedFile.type,
    //     documentId: null, // Will be set after upload
    //   },
    // };

    // // Add dummy user message immediately
    // setMessages((prev) => {
    //   const newMessages = [...prev, dummyMessage];
    //   setLastMessageCount(newMessages.length);
    //   return newMessages;
    // });
    debouncedScrollToBottom();

    // Update chat status to processing
    updateChatStatus(selectedChat.id, "processing");
    setDocumentProcessing(true);

    try {
      // Upload using documentService (like modal)
      const result: StreamUploadResult = await documentService.uploadSingleFile(
        selectedFile,
        tenantId!,
        brandId,
        userId,
        {
          enableCompression: true,
          priority: 1,
          metadata: {
            uploadedBy: "chat-upload",
            tenantName: selectedChat.tenant,
          },
        }
      );

      // Update dummy message with documentId
      // setMessages((prev) =>
      //   prev.map((msg) =>
      //     msg.id === dummyMessage.id
      //       ? {
      //           ...msg,
      //           metadata: { ...msg.metadata, documentId: result.documentId },
      //         }
      //       : msg
      //   )
      // );
      setUploadedDocumentId(result.documentId);

      // startDocumentPolling();
   

      // Start document polling for ingestion status
      startDocumentPolling();
      // console.log("documentpollingnnngngnngn");

      // Set file as selected but not yet processed (for UI)
      setFileSelectedButNotUploaded(false);

      // Start chat message polling too (in case backend generates a response)
      // setWaitingForReply(true);

      // startPollingForReply(selectedChat.id);
    } catch (err) {
      console.error("Failed to upload document:", err);

      let errorMessage = "Failed to upload document. Please try again.";
      if (err && typeof err === "object" && "response" in err) {
        const response = (err as any).response;
        if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.statusText) {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
      } else if (err && typeof err === "object" && "message" in err) {
        errorMessage = (err as Error).message;
      }

      toast.error(errorMessage);

      // Remove dummy user message on error
      // setMessages((prev) => {
      //   const filtered = prev.filter((msg) => msg.id !== dummyMessage.id);
      //   setLastMessageCount(filtered.length);
      //   return filtered;
      // });

      // Clear states on error
      setFileSelectedButNotUploaded(false);
      setSelectedFile(null);
      setFilePreview(null);
      setUploadingDocument(false);
      setDocumentProcessing(false);
      setUploadedDocumentId(null);
      setWaitingForReply(false);
      updateChatStatus(selectedChat.id, "error");

      // Stop pollings
      stopDocumentPolling();
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [
    selectedFile,
    selectedChat,
    uploadingDocument,
    hasPendingMessages,
    userId,
    brandId,
    tenantId,
    debouncedScrollToBottom,
    startPollingForReply,
    startDocumentPolling,
    pollingInterval,
    updateChatStatus,
    startDocumentPolling,
    stopDocumentPolling,
  ]);

  // Simplified message sending 
  const sendMessage = useCallback(async () => {
    if (!messageInput.trim() || !selectedChat || sendingMessage) return;

    // Block sending if there are pending messages or document is still processing/uploading
    if (hasPendingMessages) {
      setMessagesError(
        documentProcessing || uploadingDocument
          ? "Please wait for the document to complete processing before sending a message."
          : "Please wait for the previous message to complete before sending a new one."
      );
      return;
    }

    const messageContent = messageInput.trim();
    setSendingMessage(true);
    setMessagesError(null);
    setMessageInput(""); // Clear input immediately for better UX

    // Create user message
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      sessionId: selectedChat.id,
      query: messageContent,
      responseStatus: "PENDING",
      createdAt: new Date().toISOString(),
      // Optionally add reference to uploaded document if needed
      metadata: uploadedDocumentId
        ? { documentId: uploadedDocumentId }
        : undefined,
    };

    // Add user message immediately
    setMessages((prev) => {
      const newMessages = [...prev, userMessage];
      setLastMessageCount(newMessages.length); // Update count
      return newMessages;
    });
    debouncedScrollToBottom();

    // Update chat status to processing when sending message
    updateChatStatus(selectedChat.id, "processing");

    try {
      // Send to backend with web search parameter (no file, reference document via metadata if present)
      await chatService.sendUserMessage(
        selectedChat.id,
        messageContent,
        selectedFile || undefined, // Send the actual File object or undefined
        websearch, // Send web search preference
        selectedFile
          ? {
              fileName: selectedFile.name,
              fileSize: selectedFile.size,
              mimeType: selectedFile.type,
            }
          : undefined
      );

      // Clear file selection after sending
      setSelectedFile(null);
      setFilePreview(null);

      // Show typing indicator and start polling for reply
      setWaitingForReply(true);
      startDocumentPolling();
      startPollingForReply(selectedChat.id);
    } catch (err) {
      console.error("Failed to send message:", err);

      let errorMessage = "Failed to send message. Please try again.";
      if (err && typeof err === "object" && "response" in err) {
        const response = (err as any).response;
        if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.statusText) {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
      } else if (err && typeof err === "object" && "message" in err) {
        errorMessage = (err as Error).message;
      }

      setMessagesError(errorMessage);
      setMessageInput(messageContent); // Restore input on error

      // Remove user message on error
      setMessages((prev) => {
        const filtered = prev.filter((msg) => msg.id !== userMessage.id);
        setLastMessageCount(filtered.length);
        return filtered;
      });
      setWaitingForReply(false);

      // Update chat status to error
      updateChatStatus(selectedChat.id, "error");

      // Clear polling on error
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    } finally {
      setSendingMessage(false);
    }
  }, [
    messageInput,
    selectedChat,
    sendingMessage,
    hasPendingMessages,
    documentProcessing,
    uploadingDocument,
    websearch,
    uploadedDocumentId,
    debouncedScrollToBottom,
    startDocumentPolling,
    startPollingForReply,
    pollingInterval,
    updateChatStatus,
  ]);

  const handleDeepResearch = useCallback(() => {
    if (!selectedChat) {
      setError("No chat is Selected for deep research");
      return;
    }

    try {
      // Prepare chat data to send to deep research
      const chatData = {
        chatId: selectedChat.id,
        chatTitle: selectedChat.title,
        tenant: selectedChat.tenant,
        tenantId: selectedChat.tenantId,
        messages: messages.map((msg) => ({
          id: msg.id,
          query: msg.query,
          result: msg.result,
          responseStatus: msg.responseStatus,
          createdAt: msg.createdAt,
          updatedAt: msg.updatedAt,
          isWebSearch: msg.isWebSearch,
          metadata: msg.metadata,
        })),
        lastMessage: selectedChat.lastMessage,
        messageCount: selectedChat.messageCount,
        updatedAt: selectedChat.updatedAt,
        createdAt: selectedChat.createdAt,
      };

      // Navigate to deep search with chat ID as query parameter
      navigate(
        `/deep-search?chatId=${selectedChat.id}&tenantId=${selectedChat.tenantId}`,
        { state: { chatData } }
      );

      // Close the chat dropdown
      setOpen(false);
      setView("list");
      setSelectedChat(null);
    } catch (err) {
      console.error("Failed to prepare data for deep research:", err);
      setError("Failed to prepare data for deep research. Please try again.");
    }
  }, [selectedChat, messages, navigate]);

  // Close dropdown on outside click 
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        // setOpen(false);
        // setView("list");
        // setSelectedChat(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Smooth view transition (unchanged)
  const handleChatClick = useCallback(
    (chat: Chat) => {
      if (isAnimating) return;

      setIsAnimating(true);
      setSelectedChat(chat);
      setView("chat");
      loadMessages(chat.id);
      onChatSelect?.(chat);

      setTimeout(() => setIsAnimating(false), 300);
    },
    [isAnimating, loadMessages, onChatSelect]
  );

  // Optimized new chat creation
  const handleNewChat = useCallback(async () => {
    const currentUser = authService.getCurrentUser();

    if (!currentUser) {
      setError("You must be logged in to create a chat");
      return;
    }

    if (!tenantId) {
      setError("Tenant ID not found. Please provide a tenant ID.");
      return;
    }

    if (creatingChat) return;

    setCreatingChat(true);
    setError(null);

    try {
      const newSession = await chatService.createSession({
        tenantId: tenantId,
        title: `New Chat - ${new Date().toLocaleString()}`,
      });

      const newChat = convertSessionToChat(newSession);

      // Optimistic update
      setGroupedChats((prev) => ({
        ...prev,
        today: [newChat, ...prev.today],
      }));

      // Open the new chat with smooth transition
      handleChatClick(newChat);
      onNewChat?.(newChat);
    } catch (err: any) {
      console.error("Failed to create new chat:", err);
      if (
        err.response?.status === 400 &&
        err.response?.data?.message?.includes("access")
      ) {
        setError(
          "You don't have access to this tenant. Please contact your administrator."
        );
      } else {
        setError("Failed to create new chat. Please try again.");
      }
    } finally {
      setCreatingChat(false);
    }
  }, [tenantId, creatingChat, handleChatClick, onNewChat]);

  // Auto-resize textarea (unchanged, but now input is text, not textarea)
  // const handleInputChange = useCallback(
  //   (e: React.ChangeEvent<HTMLInputElement>) => {
  //     const input = e.target;
  //     setMessageInput(input.value);
  //   },
  //   []
  // );

  // Updated file handling: select file but don't upload yet (now for documents, single file)
  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Block file selection if there are pending messages or document operations
      if (hasPendingMessages || uploadingDocument || documentProcessing) {
        toast.error(
          "Please wait for the previous operation to complete before selecting files."
        );
        event.target.value = ""; // Clear the input
        return;
      }

      const file = event.target.files?.[0];
      if (file) {
        // Validate file size (100MB limit)
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
          toast.error(
            `File size ${(file.size / (1024 * 1024)).toFixed(
              1
            )}MB exceeds maximum allowed size of 100MB`
          );
          event.target.value = ""; // Clear the input
          return;
        }

        // Validate file type
        const allowedTypes = [
          "application/pdf",
          "text/csv",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-powerpoint",
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "text/plain",
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
          "image/webp",
          "image/svg+xml",
        ];

        if (!allowedTypes.includes(file.type)) {
          toast.error(
            `File type ${file.type} is not supported. Please select a PDF, CSV, Excel, PowerPoint, Word document, text file, or image.`
          );
          event.target.value = ""; // Clear the input
          return;
        }

        // Prevent duplicate by name + size (like modal)
        const alreadyAdded = messages.some(
          (msg) =>
            msg.metadata?.fileName === file.name &&
            msg.metadata?.fileSize === file.size
        );
        if (alreadyAdded) {
          toast.error(`File "${file.name}" is already uploaded.`);
          event.target.value = ""; // Clear the input
          return;
        }

        // Clear any previous errors
        setError(null);
        setSelectedFile(file);
        setFileSelectedButNotUploaded(true); // Mark as selected but not uploaded

        // Create preview for images
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => setFilePreview(e.target?.result as string);
          reader.readAsDataURL(file);
        } else {
          setFilePreview(null);
        }

        console.log("Document selected (not uploaded yet):", {
          name: file.name,
          type: file.type,
          size: file.size,
          sizeInMB: (file.size / (1024 * 1024)).toFixed(2) + "MB",
        });
      }
    },
    [hasPendingMessages, uploadingDocument, documentProcessing, messages]
  );

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setFilePreview(null);
    setFileSelectedButNotUploaded(false);
    setUploadingDocument(false);
    setDocumentProcessing(false);
    setUploadedDocumentId(null);
    stopDocumentPolling();
  }, [stopDocumentPolling]);

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  // Optimized key press handler
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  // Calculate total chats with memoization
  const totalChats = useMemo(
    () =>
      Object.values(groupedChats).reduce(
        (total, chats) => total + chats.length,
        0
      ),
    [groupedChats]
  );

  // Check if user is authenticated
  const isAuthenticated = authService.isAuthenticated();
  if (!isAuthenticated) {
    return null;
  }


  // Clean up polling intervals on component unmount or chat change
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      stopDocumentPolling();
    };
  }, [pollingInterval, stopDocumentPolling]);

  // Clear polling when switching chats or closing dialog (updated)
  useEffect(() => {
    if (!open || view !== "chat") {
      loadChats();
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      stopDocumentPolling();
      setWaitingForReply(false);
      // Clear document and file states when closing chat
      setFileSelectedButNotUploaded(false);
      setUploadingDocument(false);
      setDocumentProcessing(false);
      setUploadedDocumentId(null);
      setSelectedFile(null);
      setFilePreview(null);
    }
  }, [open, view, pollingInterval, stopDocumentPolling]);

  const aiMessages = ["8B is Typing", "8B is Thinking", "8B is Retrieving"];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % aiMessages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full">
      {/* Toggle Button */}
      <button
       onClick={() => setOpen(!open)}
        className={`flex items-center gap-x-2 p-2 shadow-md rounded-lg mr-3 transition-all duration-200 hover:bg-gray-50 cursor-pointer ${
          open ? "bg-gray-100" : "bg-white-100"
        }`}
      >
        {open ? (
          <FiMessageCircle className="h-4 w-4 text-blue-600" />
        ) : (
          <FiMessageCircle className="h-4 w-4 text-gray-600" />
        )}
        <span className="text-sm font-medium text-gray-600">AI Assistant</span>
        {open ? (
          <FaChevronUp
            onClick={() => setOpen(!open)}
            className="h-4 w-4 text-gray-600 cursor-pointer"
            title="All Chats"
          />
        ) : (
          <FaChevronDown
            onClick={() => setOpen(!open)}
            className="h-4 w-4 text-gray-400 cursor-pointer"
            title="All Chats"
          />
        )}
      </button>

      {/* Dropdown Panel (unchanged structure) */}
      {open && (
        <div
          ref={dropdownRef}
          className="fixed z-50 w-120 bg-white rounded-2xl shadow-lg border border-gray-200 max-h-[82vh] "
          style={{
            top: position.y,
            left: position.x,
            width: "480px",
          }}
        >
          {/* Drag Handle (unchanged) */}
          <div className="flex justify-center py-1 bg-gray-50 rounded-t-2xl">
            <PiDotsSixBold
              className="text-gray-400 hover:text-gray-600 cursor-move"
              onMouseDown={handleMouseDown}
            />
          </div>

          {view === "list" && (
            <div className="h-[600px] flex flex-col">
              {/* Header (unchanged) */}
              <div className="flex-shrink-0 p-4 border-b border-gray-100">
                <div className="flex items-center gap-x-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="Search chats..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full p-3 pr-10 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-200"
                    />
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                      <FaSearch className="h-4 w-4" />
                    </span>
                  </div>
                  <button
                    onClick={handleNewChat}
                    disabled={creatingChat || !tenantId}
                    className="flex items-center px-4 py-3 text-white bg-black text-sm rounded-xl gap-x-2  transition-all duration-200 shadow-sm disabled:opacity-50"
                  >
                    {creatingChat ? (
                      <FaSpinner className="h-4 w-4 animate-spin" />
                    ) : (
                      <GoPlus className="h-4 w-4" />
                    )}
                    <span>{creatingChat ? "Creating..." : "New Chat"}</span>
                  </button>
                  <button
                    onClick={loadChats}
                    disabled={loading}
                    className="p-3 text-gray-600 hover:text-gray-600 hover:bg-blue-50 rounded-xl transition-all duration-200"
                  >
                    <FiRefreshCw
                      className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                    />
                  </button>
                </div>

                {error && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-xs">{error}</p>
                  </div>
                )}
              </div>

              {/* Chat List (unchanged) */}
              <div className="flex-1 overflow-y-auto px-2">
                {!loading && totalChats === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <FiMessageCircle className="h-12 w-12 mb-3 text-gray-300" />
                    <p className="text-sm font-medium mb-1">No chats yet</p>
                    <p className="text-xs text-gray-400 mb-4">
                      Start a conversation to see your chat history
                    </p>
                  </div>
                ) : (
                  ["today", "yesterday", "older"].map((dateKey) => {
                    const filteredChats = filteredChatsData[dateKey] || [];
                    if (filteredChats.length === 0) return null;

                    return (
                      <div key={dateKey} className="py-2 mt-2">
                        <div className="px-2 uppercase font-light text-sm text-gray-600  pb-1">
                          {dateKey}
                        </div>
                        <div className="space-y-1 mb-2 border-b border-gray-100">
                          {filteredChats.map((chat) => {
                            // const statusDisplay = chatStatusStyles[chat.status || ChatStatus.ACTIVE];

                            return (
                              <div
                                key={chat.id}
                                onClick={() => {
                                  setView("chat");
                                  handleChatClick(chat);
                                }}
                                className="group flex justify-between cursor-pointer border-gray-100 rounded-md px-3 py-2 transition-all duration-200 hover:bg-gray-100"
                              >
                                <div className="flex min-w-0">
                                  <h4 className="text-sm max-w-[20vw] text-gray-600 truncate">
                                    {chat.lastMessage}
                                  </h4>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="py-0.5 bg-gray-200 text-xs px-3 rounded-xl">
                                      {chat.tenant}
                                    </span>

                                    {/* Chat Status Indicator */}
                                    {/* <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusDisplay.bg} ${statusDisplay.text}`}>
                                      {statusDisplay.icon}
                                      <span>{statusDisplay.label}</span>
                                    </div> */}
                                  </div>
                                </div>

                                <div className="text-xs text-gray-400">
                                  <FaAngleRight className="h-4 w-4" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Chat View (updated for document flow) */}
          {view === "chat" && (
            <div className="h-[80vh] flex flex-col bg-[#FDFDFD]">
              {/* Chat Header (updated to show status) */}
              <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                <button
                  onClick={() => setView("list")}
                  className="p-2 hover:bg-gray-200 rounded-lg"
                >
                  <FaArrowLeft className="h-4 w-4 text-gray-600" />
                </button>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold truncate">
                    {selectedChat?.tenant}
                  </h3>
                  <p className="text-xs text-gray-500 truncate">
                    {selectedChat?.title}
                  </p>
                </div>

                <button
                  onClick={handleDeepResearch}
                  className="flex items-center px-3 py-2 text-white bg-black text-sm rounded-xl gap-x-2  transition-all duration-200 shadow-sm disabled:opacity-50 cursor-pointer"
                >
                  Deep Research
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 hover:bg-gray-200 rounded-lg"
                >
                  <FaTimes className="h-4 w-4 text-gray-600" />
                </button>
              </div>

              {/* Chat Messages (updated for document dummy messages) */}
              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 space-y-3"
              >
                {loadingMessages ? (
                  <div className="flex items-center justify-center py-8 text-gray-600">
                    <FaSpinner className="h-6 w-6 animate-spin mr-2" />
                    Loading messages...
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <FiMessageCircle className="h-8 w-8 mb-2" />
                    No messages yet.
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} className="space-y-3">
                      {/* User Query (handles both regular and document dummy messages) */}
                      <div className="flex justify-end">
                        <div className="max-w-[80%] p-3 rounded-b-xl rounded-tl-xl shadow-sm bg-gray-100">
                          <p className="text-sm flex items-center">
                            {msg.query}
                          </p>

                          {/* Document/File attachment indicator */}
                          {(msg.metadata?.fileName ||
                            msg.metadata?.documentId) && (
                            <div className="mt-2 p-2 bg-gray-100 bg-opacity-20 rounded-lg">
                              <div className="flex items-center gap-2">
                                <FiPaperclip className="h-4 w-4" />
                                <span className="text-xs">
                                  {msg.metadata?.fileName ||
                                    "Uploaded document"}
                                  {msg.metadata?.documentId && (
                                    <span className="ml-1 text-gray-400">
                                      (Doc ID: {msg.metadata.documentId})
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Web search indicator */}
                          {msg.isWebSearch && (
                            <div className="mt-2 flex items-center gap-1">
                              <GoGlobe className="h-3 w-3" />
                              <span className="text-xs opacity-70">
                                Web Search Enabled
                              </span>
                            </div>
                          )}

                          <p className="text-xs mt-1 text-gray-500">
                            {new Date(msg.createdAt).toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>

                      {/* AI Response */}
                      {msg.responseStatus === "SUCCESS" && msg.result && (
                        <div className="flex justify-start">
                          <div className="max-w-[100%] p-3  text-gray-900">
                            <div className="flex justify-between items-center mb-2">
                              <h3 className="text-lg flex items-center capitalize font-semibold text-gray-800">
                                <img
                                  src="https://s3-qa-8byte.s3.ap-south-1.amazonaws.com/8Black.png"
                                  alt=""
                                  className="h-5 w-5 mr-2"
                                />{" "}
                                {msg.query}
                              </h3>
                            </div>
                            <p className="text-sm leading-6">
                              <ChatMarkdown
                                msg={msg}
                                handleSourceClick={handleSourceClick}
                              />
                            </p>
                            <p className="text-[10px] mt-1 text-gray-500">
                              AI Assistant •{" "}
                              {new Date(
                                msg.updatedAt || msg.createdAt
                              ).toLocaleTimeString([], {
                                hour: "numeric",
                                minute: "2-digit",
                              })}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Error Message */}
                      {msg.responseStatus === "FAILURE" && (
                        <div className="flex justify-start">
                          <div className="max-w-[80%] p-3 rounded-b-xl rounded-tr-xl shadow-sm bg-gray-100 text-gray-900 border border-gray-200">
                            <p className="text-sm">Failed to process</p>
                            <p className="text-xs mt-1 text-gray-600">
                              Error •{" "}
                              {new Date(
                                msg.updatedAt || msg.createdAt
                              ).toLocaleTimeString([], {
                                hour: "numeric",
                                minute: "2-digit",
                              })}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Pending Status (updated for document processing) */}
                      {msg.responseStatus === "PENDING" && (
                        <div className="flex justify-start">
                          <div className="max-w-[80%] p-3 text-gray-700">
                            <p className="text-sm flex items-center gap-2">
                              {msg.query?.includes("Uploaded document:")
                                ? "Document is being processed..."
                                : "8B is processing"}
                              <span className="flex space-x-1">
                                <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" />
                                <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce [animation-delay:0.2s]" />
                                <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce [animation-delay:0.4s]" />
                              </span>
                            </p>

                            <p className="text-xs mt-1 text-gray-600">
                              Processing •{" "}
                              {new Date(msg.createdAt).toLocaleTimeString([], {
                                hour: "numeric",
                                minute: "2-digit",
                              })}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
                {(waitingForReply) && (
                  <div className="flex items-center space-x-1 text-xs text-gray-400 rounded-b-xl rounded-tr-xl p-2">
                    <span>{aiMessages[index]}</span>
                    <span className="flex space-x-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]" />
                    </span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input (updated for document upload flow) */}
              <div className="p-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-md">
                {/* Document Preview (shown when selected but not uploaded) */}
                {selectedFile &&
                  fileSelectedButNotUploaded &&
                  !uploadingDocument && (
                    <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FiPaperclip className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-700">
                            {selectedFile.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          onClick={handleRemoveFile}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <FaTimes className="h-4 w-4" />
                        </button>
                      </div>
                      {filePreview && (
                        <img
                          src={filePreview}
                          alt="Preview"
                          className="mt-2 max-w-full h-20 object-contain rounded"
                        />
                      )}
                    </div>
                  )}

                {/* Document Upload Loading (if uploading) */}
                {uploadingDocument && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2">
                      <FaSpinner className="h-4 w-4 animate-spin text-gray-500" />
                      <span className="text-sm ">Uploading document...</span>
                    </div>
                  </div>
                )}

                {/* Document Processing Indicator (if processing) */}
                {documentProcessing && !uploadingDocument && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2">
                      <AiOutlineFileSync className="h-4 w-4 animate-spin text-gray-600" />
                      <span className="text-sm ">Analyzing document...</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <div className="items-center w-full bg-white border border-gray-300 rounded-3xl shadow-md px-4 py-3 relative">
                    <div className="flex items-center">
                      <textarea // Changed back to textarea for multi-line support
                        placeholder={
                          hasPendingMessages
                            ? documentProcessing || uploadingDocument
                              ? "Please wait for the document to complete processing..."
                              : "Please wait for the previous message to complete..."
                            : selectedFile
                            ? "Ask After Uploading "
                            : "Type a message... "
                        }
                        className="flex-1 px-3 py-2 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-neutral-50 bg-white disabled:bg-gray-100 disabled:text-gray-500 resize-none max-h-24"
                        value={messageInput}
                        onChange={(e) => {
                          setMessageInput(e.target.value);
                          // Auto-resize
                          e.target.style.height = "auto";
                          e.target.style.height =
                            Math.min(e.target.scrollHeight, 96) + "px";
                        }}
                        onKeyDown={handleKeyDown}
                        disabled={
                          hasPendingMessages ||
                          selectedFile !== null ||
                          uploadingDocument
                        }
                        rows={1}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-2 gap-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setWebSearch(!websearch)}
                          disabled={hasPendingMessages}
                          className={`text-xs px-3 py-2 flex items-center rounded-full ${
                            websearch ? "bg-gray-100" : ""
                          } shadow-sm border border-gray-200 text-black cursor-pointer transition-colors duration-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          <GoGlobe
                            className={`h-4 w-4 ${
                              websearch ? "text-blue-500" : "text-black"
                            } mr-1`}
                          />
                          Web Search
                        </button>

                        {/* Attach File Button (only if no file selected and no pending ops) */}
                        {!selectedFile &&
                          !fileSelectedButNotUploaded &&
                          !uploadingDocument &&
                          !documentProcessing && (
                            <label
                              className={`text-xs px-3 py-2 flex items-center rounded-full shadow-sm border border-gray-200 text-black transition-colors duration-200 hover:bg-gray-100 ${
                                hasPendingMessages
                                  ? "opacity-50 cursor-not-allowed"
                                  : "cursor-pointer"
                              }`}
                            >
                              <FiPaperclip className="h-4 w-4 mr-1" />
                              Attach Document
                              <input
                                type="file"
                                onChange={handleFileSelect}
                                className="hidden"
                                accept=".pdf,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp,.svg"
                                disabled={hasPendingMessages}
                              />
                            </label>
                          )}

                        {/* Upload Document Button (if file selected but not uploaded) */}
                        {selectedFile &&
                          fileSelectedButNotUploaded &&
                          !uploadingDocument && (
                            <button
                              onClick={handleFileUpload}
                              disabled={uploadingDocument || hasPendingMessages}
                              className="text-xs px-3 py-2 flex items-center bg-gray-600 text-white rounded-full shadow-sm transition-colors duration-200 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {uploadingDocument ? (
                                <FaSpinner className="h-4 w-4 animate-spin mr-1" />
                              ) : (
                                <FiPaperclip className="h-4 w-4 mr-1" />
                              )}
                              Upload Document
                            </button>
                          )}
                      </div>

                      {/* Send Message Button (only shown if no document pending, regular message) */}
                      {!fileSelectedButNotUploaded &&
                        !uploadingDocument &&
                        !documentProcessing && (
                          <button
                            type="submit"
                            className="bg-blue-600 text-white hover:bg-blue-700 hover:text-white hover:border-none disabled:border disabled:border-gray-300 rounded-full p-2 ml-3 disabled:opacity-60 disabled:bg-white disabled:text-gray-400 cursor-pointer disabled:cursor-not-allowed"
                            onClick={sendMessage}
                            disabled={
                              !messageInput.trim() ||
                              sendingMessage ||
                              hasPendingMessages
                            }
                            title={
                              hasPendingMessages
                                ? "Please wait for the previous operation to complete"
                                : "Send message"
                            }
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L12 6.414V17a1 1 0 11-2 0V6.414L4.707 10.707a1 1 0 01-1.414-1.414l6-6z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Document Polling Indicator (if active) */}
          {documentPollingInterval && (
            <div className="absolute top-2 right-2 flex items-center gap-2 text-xs text-gray-600 bg-white/80 px-2 py-1 rounded-full border border-purple-200 z-10">
              <AiOutlineFileSync className="w-3 h-3 animate-spin" />
              <span>Document Processing</span>
            </div>
          )}
        </div>
      )}

      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};

export default TenantChat;
